import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, message, Modal, Space, Tag } from 'antd';
import { WebUxPage, WebUxPageContent, WebUxPageItem } from '../../webux-layouts/WebUxPage';
import { Employee } from '../../../../entity/Employee';
import { useConnection, useUserInfo } from '../../../../connection/Application';
import {
  DataTable,
  DataTableColumns,
  DataTablePagination,
} from '../../../../components/molecules/DataTable/DataTable';
import { FormModal, useFormModal } from '../../../../components/form-modal/FormModal';
import Form, { FormSchema } from '../../../../form/Form';
import { QueryBuilder } from '../../../../components/molecules/QueryBuilder/QueryBuilder';
import { InfoView, InfoViewItem } from '../../../../components/info-view/InfoView';
import { FlexBox } from '../../../../components/atoms/FlexBox';
import Utils from '../../../../utils/Utils';
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { UserAvatar, UserNameView } from '../../../../components/atoms/UserAvatar';
import { useHistory } from 'react-router-dom';
import Link from 'antd/lib/typography/Link';
import { CycleToggle } from '../../../../components/atoms/ItemToggle';
import { PerformanceCycle } from '../../../../entity/PerformanceCycle';
import { PageHeader } from '@ant-design/pro-layout';

const searchSchema = [
  {
    id: 'email',
    name: 'Email',
    type: 'string',
  },
  {
    id: 'employeeId',
    name: 'Employee Id',
    type: 'string',
  },
  {
    id: 'epfNumber',
    name: 'EPF Number',
    type: 'string',
  },
];
// const colors = [
//   'magenta',
//   'red',
//   'volcano',
//   'orange',
//   'gold',
//   'lime',
//   'green',
//   'cyan',
//   'blue',
//   'geekblue',
//   'purple'
// ];

const roles = [
  { id: 'admin', value: 'Admin' },
  { id: 'hr', value: 'HR' },
  { id: 'management', value: 'Management' },
  { id: 'user', value: 'Normal User' },
];

const passwordResetSchema: FormSchema = [
  {
    id: 'id',
    name: '',
    type: 'hidden',
    layout: { lg: 0 },
  },
  {
    id: 'newPassword',
    name: 'New Password',
    type: 'password',
    options: {
      rules: [{ required: true, whitespace: true, message: 'Password is required' }],
    },
  },
  {
    id: 'reTypePassword',
    name: 'Re-Type Password',
    type: 'password',
    options: {
      dependencies: ['newPassword'],
      rules: [
        { required: true, whitespace: true, message: 'Password is required' },
        { validator: 'equals', to: 'newPassword', message: 'Same password required' },
      ],
    },
  },
];

export const EmployeesPage: FC = () => {
  const [tableData, setTableData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<DataTablePagination>({ page: 1, size: 10 });
  const [query, setQuery] = useState<any>({});
  const [total, setTotal] = useState<number>(0);
  const [selected, setSelected] = useState<Employee>();
  const [user, setUser] = useState<Employee>();
  const [designation, setDesignation] = useState();
  const createForm = useFormModal<Employee>();
  const editForm = useFormModal<Employee>();
  const passwordResetForm = useFormModal();
  const [cycle, setCycle] = useState<number>();
  const [cycles, setCycles] = useState<PerformanceCycle[]>([]);
  const user2 = useUserInfo();

  const connection = useConnection();
  const history = useHistory();

  const columns: DataTableColumns<Employee> = useMemo(() => {
    return [
      {
        title: 'User',
        dataIndex: 'image',
        key: 'image',
        // eslint-disable-next-line react/display-name
        render: (value, record) => (
          <Space>
            <UserAvatar src={value} />
            <Link
              onClick={() => {
                setSelected(record);
                setUser(record);
              }}>
              {record.firstName} {record.lastName}
            </Link>
          </Space>
        ),
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
      },
      // {
      //   title: 'Employee Id',
      //   dataIndex: 'employeeId',
      //   key: 'employeeId',
      // },
      {
        title: 'EPF Number',
        dataIndex: 'epfNo',
        key: 'epfNumber',
      },
      {
        title: 'Designation',
        dataIndex: 'designationName',
        key: 'designationName',
      },
      {
        title: 'Action',
        dataIndex: 'id',
        key: 'id',
        // eslint-disable-next-line react/display-name
        render: (name: Text, record: any) => (
          <Button
            onClick={() => {
              history.push(`/webux/employees/${record.id}/performance-result`);
            }}>
            Performance
          </Button>
        ),
      },
    ];
  }, [history]);

  const restPassword = useCallback(
    (value, userId) => {
      delete value.reTypePassword;
      connection
        .patch(`users/password-reset/${userId}`, value)
        .then(() => {
          passwordResetForm.reset();
          passwordResetForm.close();
          Modal.success({
            title: 'Password changed successfully',
          });
        })
        .catch(() => {
          Modal.error({
            title: 'Password not changed',
          });
        });
    },
    [connection, passwordResetForm],
  );

  const employeeEditSchema: FormSchema = useMemo(
    () => [
      { id: 'image', name: 'Image', type: 'avatar' },
      {
        id: 'firstName',
        name: 'First Name',
        type: 'string',
        layout: { span: 12 },
        options: {
          rules: [{ required: true, whitespace: true, message: 'First Name is required' }],
        },
      },
      {
        id: 'lastName',
        name: 'Last Name',
        type: 'string',
        layout: { span: 12 },
        options: {
          rules: [{ required: true, whitespace: true, message: 'Last Name is required' }],
        },
      },
      {
        id: 'email',
        name: 'Email',
        type: 'string',
        options: {
          rules: [
            { required: true, whitespace: true, message: 'Email is required' },
            { type: 'email', message: 'Invalid Email' },
          ],
        },
      },
      // {
      //   id: 'employeeId',
      //   name: 'Employee Id',
      //   type: 'string',
      //   layout: { span: 12 },
      //   options: {
      //     rules: [{ required: true, whitespace: true, message: 'Employee Id is required' }],
      //   },
      // },
      {
        id: 'epfNo',
        name: 'EPF Number',
        type: 'number',
        layout: { span: 12 },
        options: {
          rules: [{ required: true, message: 'EPF Number is required' }],
        },
      },
      {
        id: 'doj',
        name: 'Date of Join',
        type: 'date',
        layout: { span: 12 },
      },
      // {
      //   id: 'designation',
      //   name: 'Designation',
      //   type: 'select',
      //   options: {
      //     rules: [{ required: true, whitespace: true, message: 'Designation is required' }],
      //   },
      //   props: {
      //     values: ['Software Engineer', 'Quality Assurance Engineer', 'Business Analyst', 'Tech Lead'],
      //   },
      // },
      {
        id: 'goalsAvailability',
        name: 'Goals Availability',
        type: 'boolean',
        layout: { span: 12 },
      },
      {
        id: 'designation',
        name: 'Designation',
        type: 'selectSearch',
        options: {
          rules: [{ required: true, message: 'Designation is required' }],
        },
        props: {
          values: designation,
        },
      },
      {
        id: 'roles',
        name: 'Roles',
        type: 'autocomplete',
        options: {
          rules: [{ required: true, message: 'Role is required' }],
        },
        props: {
          mode: 'multiple',
          values: roles,
        },
      },
      // {
      //   id: 'supervisor',
      //   name: 'Supervisor',
      //   type: 'selectSearch',
      //   props: {
      //     source: 'user',
      //   },
      // },
    ],
    [designation],
  );

  const employeeCreateSchema: FormSchema = useMemo(
    () =>
      process.env.REACT_APP_MSAL === 'true'
        ? employeeEditSchema
        : [
            ...employeeEditSchema,
            { id: 'password', name: 'Password', type: 'password', layout: { span: 12 } },
            {
              id: 'confirmPassword',
              name: 'Confirm Password',
              type: 'password',
              layout: { span: 12 },
              options: {
                dependencies: ['password'],
                rules: [
                  { required: true, whitespace: true, message: 'Password is required' },
                  { validator: 'equals', to: 'password', message: 'Same password required' },
                ],
              },
            },
          ],
    [employeeEditSchema],
  );

  const search = useCallback(
    (query: any, pagination: DataTablePagination) => {
      setLoading(true);
      connection
        .get('users', { ...pagination, ...query })
        .then((value) => {
          setTableData(value.data);
          setTotal(value.total);
        })
        .finally(() => setLoading(false));
    },
    [connection],
  );
  const getDesignation = useCallback(
    (filter: any, pagination: DataTablePagination) => {
      connection.get('admin/designation', { ...pagination, ...filter }).then((response) => {
        setDesignation(response.designation);
      });
    },
    [connection],
  );

  useEffect(() => {
    connection
      .get<{ performanceCycle: PerformanceCycle[]; closestCycle: PerformanceCycle }>('cycles')
      .then((value) => {
        setCycle(value.performanceCycle?.[0].id);
        setCycles(value.performanceCycle);
      });
    search({}, { page: 0, size: 10 });
    getDesignation({}, { page: 1, size: 100 });
  }, [connection, getDesignation, search]);

  // useEffect(() => {
  //   const teams: any[] = [];
  //   connection.get('teams').then((val) => {
  //     val.teams.map((team: { id: number; teamName: string }, key: number) => {
  //       teams.push({ id: team.id, name: team.teamName, color: colors[key % colors.length] });
  //     });
  //   });
  //   console.log(teams);
  // }, [connection]);

  const addEmployee = useCallback(
    async (employee: Employee) => {
      const user = { ...employee, roles: [...employee.roles] };
      delete (user as any).confirmPassword;
      delete (user as any).password;
      try {
        await connection.post('users', {
          user,
          // For SSO/MSAL users, send no password so the backend assigns a random one
          // (they log in via SSO). Never ship a shared default credential in the bundle.
          password: process.env.REACT_APP_MSAL === 'true' ? '' : employee.password,
        });
        search(query, pagination);
        createForm.close();
      } catch (e: any) {
        if (e.body?.body?.errors) {
          createForm.setErrors(e.body.body.errors);
        } else {
          createForm.setErrors([]);
        }
      }
    },
    [connection, createForm, pagination, query, search],
  );
  const editEmployee = useCallback(
    async (formData) => {
      try {
        await connection.patch(`users/${user?.id}`, {
          user: { ...Utils.removeNull(formData), roles: [...formData.roles] },
        });
        search(query, pagination);
        editForm.close();
      } catch (e: any) {
        if (e.body?.body?.errors) {
          editForm.setErrors(e.body.body.errors);
        } else {
          editForm.setErrors([]);
        }
      }
    },
    [connection, editForm, pagination, query, search, user?.id],
  );

  const deleteEmployee = useCallback(
    async (id: number) => {
      Modal.confirm({
        title: 'Delete User',
        icon: <ExclamationCircleOutlined />,
        content: 'Are you sure delete this user?',
        okText: 'Delete',
        okType: 'danger',
        async onOk() {
          await connection.delete(`users/${id}`);
          setSelected(undefined);
          search(query, pagination);
          message.success('User deleted');
        },
        onCancel() {
          console.log('Cancel');
        },
      });
    },
    [connection, pagination, query, search],
  );

  const deactivateEmployee = useCallback(
    async (id: number) => {
      Modal.confirm({
        title: 'Deactivate User',
        icon: <ExclamationCircleOutlined />,
        content: 'Are you sure you want to deactivate this user?',
        okText: 'Deactivate',
        okType: 'danger',
        async onOk() {
          await connection.patch(`users/deactivate/${id}`);
          setSelected(undefined);
          search(query, pagination);
          message.success('User deactivated');
        },
        onCancel() {
          console.log('Cancel');
        },
      });
    },
    [connection, pagination, query, search],
  );

  return (
    <WebUxPage>
      <PageHeader
        ghost={false}
        style={{ background: '#FFF' }}
        title="Employees"
        subTitle="Manage employee details"
      />
      <WebUxPageContent>
        <WebUxPageItem
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <QueryBuilder
            searchPlaceholder="Search Employee"
            search={true}
            value={query}
            onChange={setQuery}
            onSearch={(value) => search(value, pagination)}
            schema={searchSchema}
          />
          <Space>
            <Button
              onClick={() => {
                setQuery(undefined);
                search(undefined, { page: 1, size: pagination.size });
              }}>
              Reset
            </Button>
            <Button type="primary" onClick={() => search(query, pagination)}>
              Search
            </Button>
          </Space>
        </WebUxPageItem>
        <WebUxPageItem>
          <Space direction={'vertical'} style={{ width: '100%' }} size={16}>
            <FlexBox horizontalAlign={'right'}>
              {(user2?.roles?.includes('admin') || user2?.roles?.includes('hr')) && (
                <Button
                  key="1"
                  type="primary"
                  onClick={() => createForm.open({ roles: ['user'], goalsAvailability: true })}>
                  <PlusOutlined /> New Employee
                </Button>
              )}
            </FlexBox>
            <DataTable<Employee>
              rowKey={'id'}
              loading={loading}
              columns={columns}
              dataSource={tableData}
              total={total}
              // onClick={(item) => {
              //   setSelected(item);
              //   setUser(item);
              // }}
              onPaginationChange={(p) => {
                setPagination(p);
                if (pagination.page !== p.page || pagination.size !== p.size) {
                  search(query, p);
                }
              }}
              dtPagination={pagination}
            />
          </Space>
        </WebUxPageItem>
      </WebUxPageContent>
      <FormModal title={'Add Employee'} onSubmit={addEmployee} form={createForm}>
        <Form.Items schema={employeeCreateSchema} />
        {/*<Upload.Dragger*/}
        {/*  action="http://localhost:3001/common/uploadFile"*/}
        {/*  onChange={(info) => console.log('INFO:', info)}*/}
        {/*/>*/}
      </FormModal>
      <FormModal title={'Edit Employee'} onSubmit={editEmployee} form={editForm}>
        <Form.Items schema={employeeEditSchema} />
      </FormModal>
      <FormModal
        title={'Password Reset'}
        onSubmit={(value) => restPassword(value, selected?.id)}
        form={passwordResetForm}>
        <Form.Items schema={passwordResetSchema} />
      </FormModal>
      <Modal
        open={!!selected}
        title="View Employee"
        destroyOnClose={true}
        onCancel={() => setSelected(undefined)}
        onOk={() => setSelected(undefined)}
        footer={
          <Space align={'end'}>
            {(user2?.roles?.includes('admin') || user2?.roles?.includes('hr')) && (
              <>
                <Button onClick={() => deleteEmployee(selected?.id ?? 0)} danger>
                  Delete
                </Button>
                <Button onClick={() => deactivateEmployee(selected?.id ?? 0)} danger>
                  Deactivate
                </Button>
                {process.env.REACT_APP_MSAL !== 'true' && (
                  <Button onClick={() => passwordResetForm.open()}>Reset Password</Button>
                )}
                <Button
                  onClick={() => {
                    history.push(
                      `/webux/employees/${selected?.id}/${encodeURIComponent(
                        selected?.firstName ?? '',
                      )} ${encodeURIComponent(selected?.lastName ?? '')}/time-line`,
                    );
                    setSelected(undefined);
                  }}>
                  TimeLine
                </Button>
                <Button
                  onClick={() => {
                    history.push(
                      `/webux/my-goals/goals/user/${selected?.goalHolderId}/${encodeURIComponent(
                        selected?.firstName ?? '',
                      )} ${encodeURIComponent(selected?.lastName ?? '')}/${selected?.id}`,
                    );
                    setSelected(undefined);
                  }}>
                  Set Goals
                </Button>
                <Button
                  onClick={() => {
                    setSelected(undefined);
                    editForm.open(user);
                  }}>
                  Edit
                </Button>
              </>
            )}
          </Space>
        }>
        <InfoView>
          <InfoViewItem fullwidth title="">
            <Space direction="vertical" align="center">
              <UserAvatar src={selected?.image} size={100} />
              {selected?.delete && <Tag color="red">Resigned</Tag>}
            </Space>
          </InfoViewItem>
          <InfoViewItem title="First Name">{selected?.firstName}</InfoViewItem>
          <InfoViewItem title="Last Name">{selected?.lastName}</InfoViewItem>
          {/*<InfoViewItem title="Employee ID">{selected?.employeeId}</InfoViewItem>*/}
          <InfoViewItem title="EPF Number">{selected?.epfNo}</InfoViewItem>
          <InfoViewItem title="Email">{selected?.email}</InfoViewItem>
          <InfoViewItem title="Designation">{selected?.designationName}</InfoViewItem>
          <InfoViewItem title="Date of Join">{selected?.doj}</InfoViewItem>
          {selected?.delete && (
            <InfoViewItem title="Resigned Date">{selected?.resignedDate}</InfoViewItem>
          )}
          <InfoViewItem title="Goals Availability">{selected?.goalsAvailability + ''}</InfoViewItem>
          {/*<InfoViewItem title="Supervisor">{selected?.supervisorName}</InfoViewItem>*/}
          <InfoViewItem title="Roles">
            {selected?.roles?.map((value1, index) => (
              <UserNameView
                name={roles.find((e) => e.id == value1)?.value ?? value1}
                tags={true}
                key={index}
              />
            ))}
          </InfoViewItem>
          <FlexBox>
            <CycleToggle
              value={parseInt(`${cycle}`)}
              onChange={(value) => setCycle(value)}
              data={cycles}
            />
          </FlexBox>
          <InfoViewItem title="Teams">
            {selected?.teams
              ?.filter((e) => e.cycleId == cycle)
              .map((value1, index) => (
                <UserNameView
                  position={value1?.position}
                  src={value1.image}
                  name={value1.name}
                  tags={true}
                  key={index}
                />
              ))}
          </InfoViewItem>
        </InfoView>
      </Modal>
    </WebUxPage>
  );
};
